import { DeleteOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Empty, Flex, message, Popconfirm } from 'antd'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  usePersonsServiceDeletePersonUserAssignmentEndpoint,
  usePersonsServiceGetListPersonUserAssignmentsEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  Permissions,
  UserWithInstitutionItemDto
} from 'src/api/requests'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { Text } from '../Typography'

import { SelectDoctors } from './components'

type Props = {
  data?: UserWithInstitutionItemDto[]
}

export const DoctorCard = ({ data }: Props) => {
  const { id } = useParams()
  const { t } = useLocales()
  const { hasPermission } = useUser()
  const queryClient = useQueryClient()

  const [modalOpen, setModalOpen] = useState(false)

  const { mutate } = usePersonsServiceDeletePersonUserAssignmentEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetListPersonUserAssignmentsEndpointKey]
      })

      message.success(t('persons.delete_doctor'))
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const doctorsIds = data?.map((doctor) => String(doctor.id)) ?? []

  const onToggleModal = () => {
    setModalOpen((prev) => !prev)
  }

  const onDelete = (doctorId: string) => {
    mutate({ personId: String(id), userId: doctorId })
  }

  return (
    <Card title={t('persons.assigned_doctors')}>
      <Flex align="center" justify="center" vertical gap={16}>
        {data?.length ? (
          data.map((doctor) => (
            <Flex
              align="center"
              justify="space-between"
              className="full-width"
              key={doctor.id}
            >
              <Text size="lg">
                {doctor.firstName} {doctor.lastName}
              </Text>

              {hasPermission(
                Permissions.PERSONS_USER_ASSIGNMENT_DELETE_INSTITUTION
              ) && (
                <Popconfirm
                  title={t('common.remove_assigned_medic', {
                    name: `${doctor.firstName} ${doctor.lastName}`
                  })}
                  description={t('common.delete_description')}
                  placement="leftTop"
                  onConfirm={() => onDelete(String(doctor.id))}
                >
                  <Button icon={<DeleteOutlined />} danger />
                </Popconfirm>
              )}
            </Flex>
          ))
        ) : (
          <Empty />
        )}

        {hasPermission(
          Permissions.PERSONS_USER_ASSIGNMENT_CREATE_INSTITUTION
        ) && <Button onClick={onToggleModal}>{t('persons.add_doctor')}</Button>}
      </Flex>

      {modalOpen && (
        <SelectDoctors doctorsIds={doctorsIds} onCancel={onToggleModal} />
      )}
    </Card>
  )
}
